import { useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { VIDEO_SEGMENT_MAX_FILE_SIZE } from '../constants';
import VideoPreview from './VideoPreview';
import FixedPopover from './FixedPopover';
import { AddSegmentType } from './types';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import useBrandKitComponent from '@/Pages/BrandKit/components/useBrandKitComponent';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import { showErrorToast } from '@/libs/toast/toast';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import Icon from '@/components/atoms/Icon';
import IntroOutroModal from '@/Pages/BrandKit/components/IntroOutro/IntroOutroModal';
import useDialog from '@/components/organisms/useDialog';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

const SUPPORTED_TYPES = '.mp4,.mov';

export default function IntroOutroPopover({
  type,
  popoverClassName = '',
  style
}: {
  type: AddSegmentType;
  popoverClassName?: string;
  style?: React.CSSProperties;
}) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const { isOpen: isModalOpen, openDialog: openModal, closeDialog: closeModal } = useDialog();
  const { isBlocked } = useEditorRestrictions();
  const isSegmentEditBlocked = isBlocked('timeline-intro-outro-edit');

  const REMOVED_BRAND_KIT_NAME = 'placeholder';
  const { clipId, clipData } = useClipsContext();
  const { brandKitStore, clipsBrandKitStore, onFilesUpload, deleteItem } = useBrandKitComponent({
    key: 'intro_outro',
    title: 'Intro / Outro',
    clipId: clipData.id,
    maxFileSize: VIDEO_SEGMENT_MAX_FILE_SIZE
  });

  const brandKitIntroOutro = useMemo(() => {
    return brandKitStore.intro_outro.map(video => getBrandKitUrl(video));
  }, [brandKitStore.intro_outro]);

  const clipBrandKitIntroOutro = useMemo(() => {
    return clipsBrandKitStore[clipId]?.intro_outro;
  }, [clipsBrandKitStore, clipId]);

  const clipIntroOutro = useMemo(() => {
    const introOutroPreviewList = clipBrandKitIntroOutro
      ? clipBrandKitIntroOutro.map(video => {
          return {
            name: video,
            url: getBrandKitUrl(video, clipData.id)
          };
        })
      : [];

    const currentIntro = clipData.asset_metadata.intro;
    if (shouldAddCurrentIntroOutroToList(currentIntro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentIntro!, name: REMOVED_BRAND_KIT_NAME });
    }
    const currentOutro = clipData.asset_metadata.outro;
    if (shouldAddCurrentIntroOutroToList(currentOutro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentOutro!, name: REMOVED_BRAND_KIT_NAME });
    }
    return introOutroPreviewList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipBrandKitIntroOutro, brandKitIntroOutro]);

  function shouldAddCurrentIntroOutroToList(
    currentIntroOutro: string | undefined,
    introOutroPreviewList: { url: string; name: string }[]
  ): boolean {
    return (
      !!currentIntroOutro &&
      currentIntroOutro.includes('brand_kit') &&
      !brandKitIntroOutro.includes(currentIntroOutro) &&
      !introOutroPreviewList.find(video => video.url === currentIntroOutro)
    );
  }

  function uploadFiles(files: File[]) {
    onFilesUpload(files).catch(() =>
      showErrorToast(
        `Oops! Uploading your intro/outro encountered an issue. Please make sure your file is in MP4 format and doesn't exceed ${VIDEO_SEGMENT_MAX_FILE_SIZE}MB, then give it another go.`
      )
    );
  }

  const isSplitsEnabled = useMemo(
    () =>
      isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }),
    [clipData]
  );

  const isVisible = type === AddSegmentType.INTRO ? !clipData.asset_metadata.intro : !clipData.asset_metadata.outro;
  if (!isVisible && !isSplitsEnabled) {
    return null;
  }

  const trigger = (
    <div className="m-auto">
      <div
        className="flex size-[42px] items-center justify-center rounded-md bg-slate-100"
        data-testid={type === AddSegmentType.INTRO ? 'add-intro-button' : 'add-outro-button'}
      >
        <Icon name="IconPlus" className="timeline-icon" width={12} height={12} />
      </div>
    </div>
  );

  if (usePlatformBrandKit) {
    return (
      <>
        <div
          className={`m-auto cursor-pointer${isSegmentEditBlocked ? 'pointer-events-none opacity-50' : ''}`}
          onClick={openModal}
        >
          {trigger}
        </div>
        <IntroOutroModal isOpen={isModalOpen} onClose={closeModal} type={type} />
      </>
    );
  }

  return (
    <div className={isSegmentEditBlocked ? 'pointer-events-none opacity-50' : ''}>
      <FixedPopover
        popoverClassName={popoverClassName}
        style={style}
        trigger={trigger}
        width="w-[382px]"
        position={type === AddSegmentType.INTRO ? 'right' : 'left'}
      >
        <div className="max-h-[500px] overflow-y-auto px-4 py-3" onMouseDown={e => e.stopPropagation()}>
          <div className="flex w-full flex-col items-center justify-center !text-slate-500">
            {!clipIntroOutro.length ? (
              <div className="h-36 w-full">
                <FilesInput
                  onFilesUpload={uploadFiles}
                  accept={SUPPORTED_TYPES}
                  title="Upload a file"
                  label="Click to browse, or drag & drop a file here"
                  icon="IconUpload"
                />
              </div>
            ) : (
              <div className="grid w-full grid-cols-2 gap-2">
                {clipIntroOutro.map(video => (
                  <VideoPreview
                    url={video.url}
                    type={type}
                    onDelete={video.name === REMOVED_BRAND_KIT_NAME ? undefined : () => deleteItem(video.name)}
                    key={video.url}
                  />
                ))}
                <div className="aspect-video">
                  <FilesInput onFilesUpload={onFilesUpload} accept={SUPPORTED_TYPES} label="" icon="IconUpload" />
                </div>
              </div>
            )}
          </div>
          <div className="mt-[14px]">
            <div className="flex items-center gap-1">
              <Icon name="IconMovie" className="size-3" />
              <div className="text-[13px] font-medium">Brand Intro/Outro</div>
            </div>
            {brandKitStore.intro_outro.length ? (
              <div className="mt-2 grid w-full grid-cols-2 gap-2">
                {brandKitIntroOutro.map(video => (
                  <VideoPreview url={video} key={video} type={type} />
                ))}
              </div>
            ) : (
              <div className="mt-1.5 flex w-full items-center justify-center space-x-2 rounded-md bg-slate-100 py-5 text-center text-xs text-slate-500">
                <Icon name="IconTag" className="size-4" />
                <span>
                  Upload branded intros and outros in{' '}
                  <Link to="/brand-kit" className="underline hover:no-underline">
                    Brand Kit
                  </Link>
                </span>
              </div>
            )}
          </div>
        </div>
      </FixedPopover>
    </div>
  );
}
